import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import {
  FileText, Shield, MessageCircle, CreditCard,
  ArrowRight, Check, Sparkles, Users, Zap, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteFooter from "../components/SiteFooter";
import PublicPageHeader from "../components/PublicPageHeader";

import { getCurrentFilingYear, getRecentFilingYears } from "../hooks/filingYear";

const getRole = (u) => u?.user_type || u?.role || u?.type || null;

const LandingPage = () => {
  const { user, aiFilingEnabled: aiEnabled, flagLoading } = useAuth();
  const navigate  = useNavigate();

  const [plans, setPlans]               = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  

  useEffect(() => {
    // Check AI Filing feature flag

    // Fetch plans and offers (public endpoints)
    Promise.all([
      fetch(`${API}/plans`).then((r) => r.json()).catch(() => []),
      fetch(`${API}/offers/active`).then((r) => r.json()).catch(() => []),
    ]).then(([plansData, offersData]) => {
      if (Array.isArray(plansData)) setPlans(plansData);
      if (Array.isArray(offersData)) setActiveOffers(offersData);
    }).finally(() => setPlansLoading(false));
  }, []);

  const getBestOffer = (planId) => {
    const applicable = activeOffers.filter(
      (o) => !o.applicable_plans || o.applicable_plans.length === 0 ||
             o.applicable_plans.includes(planId)
    );
    if (!applicable.length) return null;
    return applicable.reduce((best, o) => {
      const val     = o.discount_type === "percentage" ? o.discount_value : o.discount_value;
      const bestVal = best.discount_type === "percentage" ? best.discount_value : best.discount_value;
      return val > bestVal ? o : best;
    });
  };

  const getDiscountedPrice = (plan, offer) => {
    if (!offer) return plan.price;
    if (offer.discount_type === "percentage") {
      return Math.max(0, plan.price - (plan.price * offer.discount_value / 100));
    }
    return Math.max(0, plan.price - offer.discount_value);
  };

  const goToDashboard = () => {
    const role = getRole(user);
    navigate(role === "admin" ? "/admin" : "/dashboard");
  };

  // Features section — CA Assisted
  const caFeatures = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Easy Document Upload",
      description: "Securely upload all your tax documents in one place",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Expert Review",
      description: "Our tax experts review every document thoroughly",
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Direct Communication",
      description: "Chat directly with your assigned tax consultant",
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Transparent Pricing",
      description: "No hidden fees, pay only for what you need",
    },
  ];

  // Steps — shown when AI Filing is OFF (original flow)
  const caSteps = [
    { num: "01", title: "Create Account",    desc: "Sign up and verify your email" },
    { num: "02", title: "Choose Plan",       desc: "Select the right plan for your needs" },
    { num: "03", title: "Upload Documents",  desc: "Submit required documents securely" },
    { num: "04", title: "Get Filed",         desc: "We handle the rest, you relax" },
  ];

  // AI Filing steps
  const aiSteps = [
    { num: "01", title: "Upload Documents",  desc: "Form 16, 26AS, bank statements & more" },
    { num: "02", title: "AI Analyses",       desc: "AI reads every document instantly" },
    { num: "03", title: "Best Regime",       desc: "Old vs New — AI picks lower tax" },
    { num: "04", title: "Download JSON",     desc: "File directly on incometax.gov.in" },
  ];

  // While feature flag is loading, show header + skeleton hero
  // This prevents the flash where landing page shows without AI then jumps
  if (flagLoading) {
    return (
      <div className="min-h-screen">
        <header className="glass-header px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--primary)" }}>
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}>MoolTax</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="font-medium hover:opacity-70">Login</Link>
              <button className="btn-primary px-4 py-2 rounded-lg text-sm" onClick={() => navigate("/register")}>Get Started</button>
            </div>
          </div>
        </header>
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="h-14 rounded-xl bg-muted animate-pulse mb-6 w-3/4" />
              <div className="h-6 rounded-lg bg-muted animate-pulse mb-3 w-full" />
              <div className="h-6 rounded-lg bg-muted animate-pulse mb-8 w-2/3" />
              <div className="flex gap-4">
                <div className="h-12 w-36 rounded-full bg-muted animate-pulse" />
                <div className="h-12 w-28 rounded-full bg-muted animate-pulse" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen">

      {/* ── Header ── */}
      <header className="glass-header px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "var(--primary)" }}
            >
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span
              className="text-2xl font-bold"
              style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
            >
              MoolTax
            </span>
          </Link>

          <nav className="flex items-center gap-4">
            {user ? (
              <Button data-testid="dashboard-btn" onClick={goToDashboard} className="btn-primary">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="font-medium hover:opacity-70" data-testid="login-link">
                  Login
                </Link>
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate("/register")}
                  className="btn-primary"
                >
                  Get Started
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════
          HERO — Two paths when AI Filing is ON
          Single path when AI Filing is OFF
          ══════════════════════════════════════════════════════ */}
      {aiEnabled ? (
        // ── AI Filing ON — Two path hero ──────────────────────
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">

            {/* Headline */}
            <div className="text-center mb-14">
              <h1
                className="text-5xl md:text-6xl font-bold mb-5 leading-tight"
                style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
              >
                ITR Filing Made Simple for{" "}
                <span style={{ color: "var(--accent)" }}>Salaried Individuals & Businesses</span>    
              </h1>
              <p className="text-xl opacity-75 max-w-2xl mx-auto leading-relaxed">
                Choose how you want to file — let our AI do it in minutes,
                or get expert CA assistance at every step.
              </p>
            </div>

            {/* Two path cards */}
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">

              {/* Card 1 — AI Filing */}
              <div
                className="rounded-2xl p-8 flex flex-col relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, var(--primary) 0%, #1a4d35 100%)",
                  color: "white",
                }}
              >
                {/* New badge */}
                <div
                  className="absolute top-5 right-5 text-xs font-bold px-3 py-1 rounded-full"
                  style={{ backgroundColor: "var(--accent)", color: "white" }}
                >
                  NEW ✨
                </div>

                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                >
                  <Sparkles className="w-7 h-7 text-white" />
                </div>

                <h2
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  AI Tax Filing
                </h2>
                <p className="opacity-80 mb-5 text-sm leading-relaxed">
                  Upload your documents and our AI analyses them, selects the best
                  tax regime, computes your tax, and generates a ready-to-submit
                  ITR JSON — in under 2 minutes. Perfect for individuals,
                  salaried professionals, and CAs managing multiple clients.
                </p>

                {/* AI highlights */}
                <ul className="space-y-2 mb-7 flex-1">
                  {[
                    "Best for salaried individuals (ITR-1 / ITR-2)",
                    "Old vs New regime — AI picks lower tax",
                    "Supports Form 16, 26AS, common deductions",
                    "FY 2024-25 Budget changes applied",
                    "Download ITR JSON, file yourself",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
                      <span className="opacity-85">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Who it is for */}
                <div
                  className="flex items-center gap-2 text-xs mb-6 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                >
                  <Users className="w-4 h-4 opacity-70" />
                  <span className="opacity-80">
                    For individuals, salaried professionals &amp; CAs
                  </span>
                </div>

                <Button
                  onClick={() => navigate(user ? "/ai-filing" : "/register?redirect=ai-filing")}
                  className="w-full font-semibold py-3 text-base"
                  style={{ backgroundColor: "var(--accent)", color: "white" }}
                  data-testid="hero-ai-filing"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {user ? "Start AI Filing" : "Try AI Filing"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Card 2 — CA Assisted */}
              <div
                className="rounded-2xl p-8 flex flex-col"
                style={{
                  backgroundColor: "var(--background)",
                  border: "1.5px solid var(--border)",
                }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: "var(--secondary)", color: "var(--accent)" }}
                >
                  <Shield className="w-7 h-7" />
                </div>

                <h2
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
                >
                  CA Assisted Filing
                </h2>
                <p className="opacity-65 mb-5 text-sm leading-relaxed">
                  Upload your documents and get assigned to a qualified Chartered
                  Accountant who reviews everything, communicates directly with you,
                  and ensures your return is filed accurately with expert oversight.
                </p>

                {/* CA highlights */}
                <ul className="space-y-2 mb-7 flex-1">
                  {[
                    "Dedicated CA reviews your documents",
                    "Direct messaging with your consultant",
                    "Handles complex cases and notices",
                    "End-to-end filing support",
                    "Best for business income and HNI",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        style={{ color: "var(--accent)" }}
                      />
                      <span className="opacity-75">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Who it is for */}
                <div
                  className="flex items-center gap-2 text-xs mb-6 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: "var(--secondary)", color: "var(--foreground)" }}
                >
                  <Users className="w-4 h-4 opacity-50" />
                  <span className="opacity-60">
                    For individuals, businesses &amp; complex tax situations
                  </span>
                </div>

                <Button
                  onClick={() => navigate(user ? "/plans" : "/register")}
                  variant="outline"
                  className="w-full font-semibold py-3 text-base"
                  style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
                  data-testid="hero-ca-assisted"
                >
                  {user ? "View CA Plans" : "Get CA Assistance"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Social proof / trust line */}
            <p className="text-center text-sm opacity-40 mt-8">
              Built for salaried professionals, freelancers, business owners and CAs across India
            </p>
          </div>
        </section>

      ) : (
        // ── AI Filing OFF — Original single path hero ─────────
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl animate-fade-in-up">
              <h1
                className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
                style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
              >
                Tax Filing Made{" "}
                <span style={{ color: "var(--accent)" }}>Simple</span>
              </h1>

              <p
                className="text-xl opacity-90 mb-8 leading-relaxed"
                style={{ color: "var(--foreground)" }}
              >
                File your income tax return easily with guided steps, 
                secure document upload, and expert assistance — no confusion, no manual errors.
              </p>

              <div className="flex flex-wrap gap-4">
                {!user ? (
                  <>
                    <Button
                      data-testid="hero-get-started"
                      onClick={() => navigate("/register")}
                      className="px-8 py-4 text-lg font-semibold rounded-full"
                      style={{ backgroundColor: "var(--accent)", color: "white" }}
                    >
                      Start Filing <ArrowRight className="inline ml-2" />
                    </Button>
                    <Button
                      data-testid="hero-login"
                      onClick={() => navigate("/login")}
                      variant="outline"
                      className="px-8 py-4 text-lg font-semibold rounded-full"
                      style={{
                        borderColor: "var(--border)",
                        color: "var(--primary)",
                        backgroundColor: "transparent",
                      }}
                    >
                      Login
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="hero-continue"
                    onClick={goToDashboard}
                    className="px-8 py-4 text-lg font-semibold rounded-full btn-primary"
                  >
                    Continue <ArrowRight className="inline ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          AI FILING HOW IT WORKS — only when AI Filing is ON
          ══════════════════════════════════════════════════════ */}
      {aiEnabled && (
        <section
          className="py-16 px-6"
          style={{ backgroundColor: "var(--secondary)" }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5" style={{ color: "var(--accent)" }} />
              <h2
                className="text-3xl font-bold text-center"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                How AI Filing Works
              </h2>
            </div>
            <p className="text-center opacity-60 mb-10 text-sm">
              From document upload to ITR JSON — fully automated, under 2 minutes
            </p>
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {aiSteps.map((step, idx) => (
                <div key={idx} className="text-center">
                  <div
                    className="text-5xl font-bold opacity-15 mb-3"
                    style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
                  >
                    {step.num}
                  </div>
                  <h3
                    className="text-base font-semibold mb-1"
                    style={{ color: "var(--primary)" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm opacity-60">{step.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA for AI Filing */}
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate(user ? "/ai-filing" : "/register?redirect=ai-filing")}
                className="px-8 py-3 font-semibold"
                style={{ backgroundColor: "var(--primary)", color: "white" }}
              >
                <Zap className="w-4 h-4 mr-2" />
                {user ? "Start AI Filing Now" : "Try AI Filing — Register Free"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          FEATURES — CA Assisted (always shown)
          ══════════════════════════════════════════════════════ */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-4xl font-bold text-center mb-4"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {aiEnabled ? "CA Assisted Filing — Features" : "Why Choose MoolTax?"}
          </h2>
          <p className="text-center text-lg opacity-70 mb-12 max-w-2xl mx-auto">
            {aiEnabled
              ? "For those who prefer expert human oversight at every step"
              : "We combine technology with expertise to deliver a seamless tax filing experience"}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {caFeatures.map((feature, idx) => (
              <div
                key={idx}
                className="card-hover rounded-2xl p-6 border animate-fade-in-up"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--background)",
                  animationDelay: `${idx * 0.1}s`,
                }}
                data-testid={`feature-card-${idx}`}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "var(--secondary)", color: "var(--accent)" }}
                >
                  {feature.icon}
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: "var(--primary)" }}
                >
                  {feature.title}
                </h3>
                <p className="opacity-70" style={{ color: "var(--foreground)" }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS — CA Assisted steps (always shown)
          ══════════════════════════════════════════════════════ */}
      <section
        className="py-20 px-6"
        style={{ backgroundColor: "var(--primary)", color: "white" }}
      >
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {aiEnabled ? "CA Assisted — How It Works" : "How It Works"}
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {caSteps.map((step, idx) => (
              <div key={idx} className="text-center" data-testid={`step-${idx}`}>
                <div
                  className="text-6xl font-bold opacity-20 mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {step.num}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="opacity-70">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          PRICING (always shown, fetched from backend)
          ══════════════════════════════════════════════════════ */}
      {(plansLoading || plans.length > 0) && (
        <section className="py-20 px-6" style={{ backgroundColor: "var(--secondary)" }}>
          <div className="max-w-7xl mx-auto">
            <h2
              className="text-4xl font-bold text-center mb-4"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Simple, Transparent Pricing
            </h2>
            <p className="text-center text-lg opacity-70 mb-12 max-w-2xl mx-auto">
              Choose the plan that fits your tax situation. No hidden charges.
            </p>

            {plansLoading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : (
              <>
                <div
                  className={`grid gap-6 max-w-6xl mx-auto ${
                    plans.length === 1
                      ? "md:grid-cols-1 max-w-sm"
                      : plans.length === 2
                      ? "md:grid-cols-2 max-w-2xl"
                      : plans.length <= 4
                      ? "md:grid-cols-2 lg:grid-cols-4"
                      : "md:grid-cols-2 lg:grid-cols-3"
                  }`}
                >
                  {plans.map((plan) => {
                    const bestOffer       = getBestOffer(plan.id);
                    const discountedPrice = getDiscountedPrice(plan, bestOffer);
                    const hasDiscount     = bestOffer && discountedPrice < plan.price;
                    const isAIPlan        = plan.plan_type === "ai_filing";

                    return (
                      <div
                        key={plan.id}
                        className="bg-white rounded-2xl flex flex-col overflow-hidden"
                        style={{
                          border: isAIPlan
                            ? "2px solid var(--accent)"
                            : "0.5px solid var(--border)",
                          boxShadow: isAIPlan
                            ? "0 4px 20px rgba(194,94,0,0.15)"
                            : "0 1px 3px rgba(0,0,0,0.06)",
                        }}
                      >
                        {/* AI plan badge */}
                        {isAIPlan && (
                          <div
                            className="text-center text-xs font-bold py-2 text-white"
                            style={{ backgroundColor: "var(--accent)" }}
                          >
                            ✨ AI POWERED — FILE YOURSELF IN MINUTES
                          </div>
                        )}

                        {/* Card header */}
                        <div
                          className="px-6 pt-6 pb-5"
                          style={{
                            borderBottom: "0.5px solid var(--border)",
                            background: isAIPlan
                              ? "linear-gradient(135deg, #fff8f0 0%, #fff 100%)"
                              : plan.plan_type === "salary"
                              ? "linear-gradient(135deg, #f8f9fa 0%, #fff 100%)"
                              : "linear-gradient(135deg, #fff8f0 0%, #fff 100%)",
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span
                              className="text-xs font-semibold px-3 py-1 rounded-full"
                              style={{
                                backgroundColor: isAIPlan
                                  ? "#fff3e0"
                                  : plan.plan_type === "salary"
                                  ? "var(--secondary)"
                                  : "#fff3e0",
                                color: isAIPlan
                                  ? "var(--accent)"
                                  : plan.plan_type === "salary"
                                  ? "var(--accent)"
                                  : "#e65100",
                              }}
                            >
                              {isAIPlan ? "AI Filing" : plan.plan_type === "salary" ? "Salaried" : "Business"}
                            </span>
                            {hasDiscount && (
                              <span
                                className="text-xs font-semibold px-2 py-1 rounded-full"
                                style={{ backgroundColor: "#fce4ec", color: "#c62828" }}
                              >
                                {bestOffer.discount_type === "percentage"
                                  ? `${bestOffer.discount_value}% OFF`
                                  : `₹${bestOffer.discount_value} OFF`}
                              </span>
                            )}
                          </div>

                          <h3
                            className="text-lg font-bold mb-1"
                            style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
                          >
                            {plan.name}
                          </h3>
                          <p className="text-sm mb-4" style={{ opacity: 0.6, lineHeight: 1.5 }}>
                            {plan.description}
                          </p>

                          <div className="flex items-end gap-2">
                            <span
                              className="text-3xl font-bold"
                              style={{ color: "var(--accent)", fontVariantNumeric: "tabular-nums" }}
                            >
                              ₹{discountedPrice.toLocaleString()}
                            </span>
                            {hasDiscount && (
                              <span className="text-base mb-1 line-through" style={{ opacity: 0.4 }}>
                                ₹{plan.price.toLocaleString()}
                              </span>
                            )}
                          </div>
                          {hasDiscount && (
                            <p className="text-xs mt-1" style={{ color: "#2e7d32" }}>
                              Use code <strong>{bestOffer.code}</strong> at checkout
                            </p>
                          )}
                        </div>

                        {/* Features list */}
                        <div className="px-6 py-5 flex-grow">
                          <ul className="space-y-3">
                            {(plan.features || []).slice(0, 5).map((f, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <Check
                                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                                  style={{ color: "var(--accent)" }}
                                />
                                <span style={{ opacity: 0.8 }}>{f}</span>
                              </li>
                            ))}
                            {(plan.features || []).length > 5 && (
                              <li className="text-xs" style={{ opacity: 0.5, paddingLeft: "22px" }}>
                                +{plan.features.length - 5} more features
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* CTA */}
                        <div className="px-6 pb-6">
                          <Button
                            onClick={() => navigate(user ? "/plans" : "/register")}
                            className="w-full"
                            style={
                              isAIPlan
                                ? { backgroundColor: "var(--accent)", color: "white" }
                                : {}
                            }
                            data-testid={`plan-cta-${plan.id}`}
                          >
                            {isAIPlan
                              ? user ? "Start AI Filing" : "Try AI Filing"
                              : user ? "Select Plan" : "Get Started"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {activeOffers.length > 0 && (
                  <p className="text-center text-sm mt-6" style={{ color: "#2e7d32" }}>
                    Special offers active — discounts applied automatically at checkout
                  </p>
                )}
                <p className="text-center text-sm opacity-50 mt-2">
                  All prices are inclusive of GST.
                </p>
              </>
            )}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          FINAL CTA
          ══════════════════════════════════════════════════════ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-4xl font-bold mb-4"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Ready to File Your Taxes?
          </h2>
          <p className="text-lg opacity-70 mb-8">
            {aiEnabled
              ? "Choose AI Filing for speed or CA Assistance for expert oversight — both on one platform."
              : "Upload documents, track progress, and get expert assistance — all in one place."}
          </p>

          {!user ? (
            <div className="flex flex-wrap gap-4 justify-center">
              {aiEnabled && (
                <Button
                  onClick={() => navigate("/register")}
                  className="px-10 py-4 text-base font-semibold rounded-full"
                  style={{ backgroundColor: "var(--accent)", color: "white" }}
                  data-testid="cta-ai-filing"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Try AI Filing Free
                </Button>
              )}
              <Button
                data-testid="cta-get-started"
                onClick={() => navigate("/register")}
                className={`px-10 py-4 text-base font-semibold rounded-full ${aiEnabled ? "" : "btn-primary"}`}
                variant={aiEnabled ? "outline" : "default"}
                style={aiEnabled ? { borderColor: "var(--primary)", color: "var(--primary)" } : {}}
              >
                {aiEnabled ? "Get CA Assistance" : "Get Started Today"}
              </Button>
            </div>
          ) : (
            <Button
              data-testid="cta-continue"
              onClick={goToDashboard}
              className="btn-primary px-12 py-4 text-lg rounded-full"
            >
              Continue
            </Button>
          )}
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-semibold mb-3">
            Learn more about Income Tax Filing
          </h2>

          <div className="flex flex-wrap justify-center gap-3 text-sm">
            <Link to="/how-to-file-itr-using-form-16" className="text-primary hover:underline">
              File ITR using Form 16
            </Link>
            <Link to="/old-vs-new-tax-regime" className="text-primary hover:underline">
              Old vs New Tax Regime
            </Link>
            <Link to="/itr-filing-for-salaried-employees" className="text-primary hover:underline">
              ITR for Salaried Employees
            </Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default LandingPage;